/**
 * SHOWICT Quantum — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults; todo consumo de
 * config pasa por resolveConfig() (patrón E.2 del Standard) para que ninguna
 * clave llegue undefined aunque la config persistida sea parcial o corrupta.
 *
 * Los parámetros del motor (pivotLen/atrMult/atrLen) tienen defaults
 * IDÉNTICOS al defaultConfig del motor (`./engine.js`, extraído byte-idéntico
 * del indicador legacy `showi_ict`, que sigue en el catálogo y coexistiendo
 * en esta entrega): el rediseño solo cambia la presentación, no la detección.
 * `slPadding` y `tpMults` son claves NUEVAS de la capa de derivación
 * (entry/SL/TP) que el motor no conoce: nunca cruzan la frontera (adapter.js
 * traduce solo las 3 claves del motor). Los colores se guardan como `null`
 * (= usar paleta Quantum) y se materializan con resolveColor al pintar o al
 * mostrar el picker.
 */

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  accent: '#B08D57',
  neutral: '#374151',
})

// Color de los marcadores de fake break ('F'): el naranja del legacy.
export const FAKE_FALLBACK_COLOR = '#ff9800'

export const SAFE_DEFAULTS = Object.freeze({
  // v1→v2: nuevo flag invalidateOnOppositeSignal. v2→v3: nuevo flag
  // fadeClosedSignals. La migración de useIndicatorConfig
  // (migrateIndicatorConfig) preserva TODAS las prefs del usuario — NUNCA
  // subir _version sin su migración explícita.
  _version: 3,

  // ── Activación interna (sin toggle en el panel): false para configs
  // nuevas; el menú de indicadores pone enabled=true en la primera
  // activación. detect/render devuelven vacío con enabled === false. ────────
  enabled: false,

  // ── Visualización ────────────────────────────────────────────────────────
  showLevels: true,        // niveles activos H/L del motor
  showHHHL: true,          // etiquetas HH/LH/HL/LL en los swings
  showSignals: true,       // etiquetas históricas con el texto original del motor
  showFakes: true,         // marcadores 'F' (solo pintura)
  showZones: true,         // zonas riesgo/beneficio de la señal activa
  showOutcomeMarkers: true, // ✓/✗ en el cierre de cada señal
  showLastSignals: true,   // tabla de últimas 10 en el badge
  fadeClosedSignals: true, // señales cerradas atenuadas (zonas/líneas/etiqueta)

  // ── Filtro opcional de activos favoritos: [] = TODOS los símbolos de la
  // plataforma (default); con valores, solo esos. Sin gating por timeframe:
  // el indicador corre siempre en el TF del chart. ──────────────────────────
  assets: [],

  // ── Budget DUAL (dimensión extra sobre el molde PRQ): un budget por grado
  // de señal. null = sin budget configurado; sin budget el dashboard muestra
  // el PnL del historial en R, con budget > 0 la estimación monetaria. ───────
  budgetAp: null, // grado A+ (BOS)
  budgetA: null,  // grado A (CHoCH)

  // ── Notificaciones (el envío real requiere tarea backend; aquí solo se
  // persisten las preferencias). Señal nueva con toggle independiente por
  // grado; TP/SL comunes a ambos grados. ────────────────────────────────────
  notificationsEnabled: false,
  notifyOnSignalAp: true,
  notifyOnSignalA: true,
  notifyOnTP1: true,
  notifyOnTP2: true,
  notifyOnTP3: true,
  notifyOnSL: true,

  // ── Estilo de línea ──────────────────────────────────────────────────────
  lineWidth: 1.5, // 1 | 1.5 | 2
  tp2LineStyle: 'dashed', // 'solid' | 'dashed'

  // ── Colores (null = paleta Quantum) ──────────────────────────────────────
  // sl: bear · tp: bull · entry: neutral · zone: bear/bull según lado
  // fake: naranja legacy
  slColor: null,
  tpColor: null,
  entryColor: null,
  zoneColor: null,
  fakeColor: null,

  // ── Capa de derivación (NUEVA, no existe en el motor): SL tras el swing
  // relevante + slPadding × ATR; TPs a R-múltiplos de |entry − sl|; e
  // invalidación por señal contraria (una señal A/A+ opuesta cierra las
  // abiertas en dirección contraria con outcome 'invalidada'). ──────────────
  slPadding: 0.5, // × ATR
  tpMults: { tp1: 1, tp2: 2, tp3: 3 }, // R-múltiplos
  invalidateOnOppositeSignal: true, // false = comportamiento pre-v1.2

  // ── Parámetros del motor (defaults = defaultConfig del motor; NO añadir,
  // renombrar ni reinterpretar) ────────────────────────────────────────────
  pivotLen: 5,
  atrMult: 1.5,
  atrLen: 14,

  // ── Dashboard / panel ────────────────────────────────────────────────────
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
})

// ── Rangos del panel ────────────────────────────────────────────────────────
export const PIVOT_LEN_RANGE = Object.freeze({ min: 2, max: 30 })
export const ATR_MULT_RANGE = Object.freeze({ min: 0.5, max: 5 })
export const ATR_LEN_RANGE = Object.freeze({ min: 2, max: 100 })
export const SL_PADDING_RANGE = Object.freeze({ min: 0, max: 2 })
export const TP_MULT_RANGE = Object.freeze({ min: 0.5, max: 10 })

export const LINE_WIDTHS = Object.freeze([1, 1.5, 2])
export const TP2_LINE_STYLES = Object.freeze(['solid', 'dashed'])

// Símbolos base soportados por la plataforma (crypto de SymbolSelector.jsx).
// Se ofrecen como favoritos opcionales; la lista vacía en config = todos.
export const AVAILABLE_SYMBOLS = Object.freeze(['BTC', 'ETH', 'SOL', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK'])

// Opacidad fija de las zonas riesgo/beneficio (spec).
export const ZONE_OPACITY = 0.07

// Atenuación de señales CERRADAS (v1.3.0): multiplicador de alpha sobre los
// colores de zonas, líneas de nivel y la etiqueta-recuadro de la señal. Los
// marcadores de outcome (✓/✗/TP sticks/Inv) NO se atenúan — son la
// información. Con fadeClosedSignals === false todo va a intensidad plena.
export const CLOSED_SIGNAL_ALPHA = 0.4

// Fuente serif de las etiquetas de señal (misma convención que la caja de
// posición de supertrend_quantum).
export const LABEL_FONT = "'Playfair Display', Georgia, serif"

// ── Helpers internos de saneado ─────────────────────────────────────────────

function clampNumber(v, min, max, fallback) {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

function asEnum(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback
}

function asColorOrNull(v) {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function asAssets(v) {
  if (!Array.isArray(v)) return [...SAFE_DEFAULTS.assets]
  // Filtra strings no válidos; el array vacío es VÁLIDO (= todos los
  // símbolos), no se cae a defaults.
  return v.filter(x => typeof x === 'string' && AVAILABLE_SYMBOLS.includes(x))
}

function asBudget(v) {
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) && n > 0 ? n : null
}

function asTpMults(v) {
  const raw = v && typeof v === 'object' ? v : {}
  return {
    tp1: clampNumber(raw.tp1, TP_MULT_RANGE.min, TP_MULT_RANGE.max, SAFE_DEFAULTS.tpMults.tp1),
    tp2: clampNumber(raw.tp2, TP_MULT_RANGE.min, TP_MULT_RANGE.max, SAFE_DEFAULTS.tpMults.tp2),
    tp3: clampNumber(raw.tp3, TP_MULT_RANGE.min, TP_MULT_RANGE.max, SAFE_DEFAULTS.tpMults.tp3),
  }
}

/**
 * Normaliza un símbolo de chart ('BTC/USDT:USDT', 'BTC/USDT', 'BTCUSDT',
 * 'btc'…) a su base en mayúsculas ('BTC') para comparar con cfg.assets.
 */
export function normalizeSymbol(rawSymbol) {
  if (typeof rawSymbol !== 'string' || rawSymbol.length === 0) return null
  let s = rawSymbol.toUpperCase().trim()
  // Quita sufijos de par conocidos, del más específico al más general.
  for (const suffix of ['/USDT:USDT', '/USDT', 'USDT']) {
    if (s.endsWith(suffix)) {
      s = s.slice(0, -suffix.length)
      break
    }
  }
  return s || null
}

/**
 * ¿El símbolo crudo del chart pasa el filtro de activos? Lista vacía = todos
 * los símbolos (semántica: los activos son un filtro opcional de favoritos).
 * Con valores, compara la base normalizada.
 */
export function symbolMatches(assets, rawSymbol) {
  if (!Array.isArray(assets)) return false
  if (assets.length === 0) return true
  const base = normalizeSymbol(rawSymbol)
  if (!base) return false
  return assets.some(a => normalizeSymbol(a) === base)
}

/**
 * Config de fábrica para el botón "Restablecer valores Quantum": defaults de
 * la CONFIG (colores, grosores, toggles visuales, budgets, parámetros
 * motor/derivación…). NUNCA toca: `enabled` (el reset no apaga ni enciende el
 * indicador — bug histórico: el reset lo eliminaba del chart al escribir
 * enabled:false), `badgePosition` (el badge no salta de sitio) ni
 * `panelSections` (acordeones del panel). NUNCA esparcir SAFE_DEFAULTS en
 * crudo sin estas tres preservaciones. La membresía del menú de indicadores
 * vive fuera de esta config.
 */
export function resetToDefaults(current) {
  const pos = current?.badgePosition
  return {
    ...SAFE_DEFAULTS,
    enabled: current?.enabled ?? SAFE_DEFAULTS.enabled,
    badgePosition: pos && Number.isFinite(pos.x) ? pos : { x: 8, y: 8 },
    panelSections:
      current?.panelSections && typeof current.panelSections === 'object'
        ? current.panelSections
        : { ...SAFE_DEFAULTS.panelSections },
  }
}

/**
 * Merge defensivo: defaults + config persistida + saneado por clave.
 * Nunca devuelve undefined en ninguna clave conocida. Si `raw` no es un
 * objeto, devuelve una copia de SAFE_DEFAULTS.
 */
export function resolveConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...SAFE_DEFAULTS }
  }
  const merged = { ...SAFE_DEFAULTS, ...raw }
  return {
    ...merged,
    enabled: asBool(merged.enabled, false),
    showLevels: asBool(merged.showLevels, true),
    showHHHL: asBool(merged.showHHHL, true),
    showSignals: asBool(merged.showSignals, true),
    showFakes: asBool(merged.showFakes, true),
    showZones: asBool(merged.showZones, true),
    showOutcomeMarkers: asBool(merged.showOutcomeMarkers, true),
    showLastSignals: asBool(merged.showLastSignals, true),
    assets: asAssets(merged.assets),
    budgetAp: asBudget(merged.budgetAp),
    budgetA: asBudget(merged.budgetA),
    notificationsEnabled: asBool(merged.notificationsEnabled, false),
    notifyOnSignalAp: asBool(merged.notifyOnSignalAp, true),
    notifyOnSignalA: asBool(merged.notifyOnSignalA, true),
    notifyOnTP1: asBool(merged.notifyOnTP1, true),
    notifyOnTP2: asBool(merged.notifyOnTP2, true),
    notifyOnTP3: asBool(merged.notifyOnTP3, true),
    notifyOnSL: asBool(merged.notifyOnSL, true),
    lineWidth: LINE_WIDTHS.includes(merged.lineWidth) ? merged.lineWidth : SAFE_DEFAULTS.lineWidth,
    tp2LineStyle: asEnum(merged.tp2LineStyle, TP2_LINE_STYLES, SAFE_DEFAULTS.tp2LineStyle),
    slColor: asColorOrNull(merged.slColor),
    tpColor: asColorOrNull(merged.tpColor),
    entryColor: asColorOrNull(merged.entryColor),
    zoneColor: asColorOrNull(merged.zoneColor),
    fakeColor: asColorOrNull(merged.fakeColor),
    slPadding: clampNumber(merged.slPadding, SL_PADDING_RANGE.min, SL_PADDING_RANGE.max, SAFE_DEFAULTS.slPadding),
    tpMults: asTpMults(merged.tpMults),
    invalidateOnOppositeSignal: asBool(merged.invalidateOnOppositeSignal, true),
    pivotLen: Math.round(clampNumber(merged.pivotLen, PIVOT_LEN_RANGE.min, PIVOT_LEN_RANGE.max, SAFE_DEFAULTS.pivotLen)),
    atrMult: clampNumber(merged.atrMult, ATR_MULT_RANGE.min, ATR_MULT_RANGE.max, SAFE_DEFAULTS.atrMult),
    atrLen: Math.round(clampNumber(merged.atrLen, ATR_LEN_RANGE.min, ATR_LEN_RANGE.max, SAFE_DEFAULTS.atrLen)),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : { x: 8, y: 8 },
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
  }
}

/** null → color de paleta; string no vacío → tal cual. */
export function resolveColor(value, fallback) {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/** Materializa los colores del indicador a partir de una config resuelta. */
export function resolveColors(cfg) {
  return {
    sl: resolveColor(cfg.slColor, QUANTUM_COLORS.bear),
    tp: resolveColor(cfg.tpColor, QUANTUM_COLORS.bull),
    entry: resolveColor(cfg.entryColor, QUANTUM_COLORS.neutral),
    fake: resolveColor(cfg.fakeColor, FAKE_FALLBACK_COLOR),
  }
}

/** hex '#RRGGBB' o 'rgb(...)' → 'rgba(r, g, b, alpha)'. */
export function withAlpha(color, alpha) {
  const a = Math.min(1, Math.max(0, alpha))
  if (typeof color !== 'string' || color.length === 0) return `rgba(0,0,0,${a})`
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${a})`)
  }
  if (color.startsWith('rgb(')) {
    const rgb = color.match(/\d+/g)
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    const n = parseInt(hex, 16)
    if (Number.isNaN(n) || hex.length < 6) return color
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  return color
}
